import {
  ArrowRight,
  CalendarDays,
  FileSpreadsheet,
  MessagesSquare,
  MousePointer2,
} from "lucide-react";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { openSupportChatWithMessage } from "@/core/pylon";
import { ProfitabilityReportPreview } from "./profitability-report-preview";

const ACCESS_MESSAGE =
  "Hi! I am interested in profitability reports. Can you help enable them for our account?";

const benefits = [
  {
    icon: CalendarDays,
    title: "Choose your reporting period",
    description:
      "Run a report for a week, a month, or any date range you need.",
  },
  {
    icon: FileSpreadsheet,
    title: "Review and share the details",
    description:
      "Open the detailed workbook or download a copy to review with your team.",
  },
  {
    icon: MessagesSquare,
    title: "Get answers about your results",
    description:
      "Ask questions about a report or compare it with a previous period.",
  },
  {
    icon: MousePointer2,
    title: "Build reports with a click",
    description:
      "Choose your dates and click Run report. Volca puts the numbers together for you.",
  },
];

export const ProfitabilityPreview = () => {
  const requestAccess = () => {
    if (!openSupportChatWithMessage(ACCESS_MESSAGE)) {
      toast.error(
        "Chat could not open. Please refresh the page and try again.",
      );
    }
  };

  return (
    <section
      aria-labelledby="profitability-preview-title"
      className="min-h-full bg-[#fbfaf8] text-primary-text"
    >
      <Stack gap="none" className="mx-auto max-w-[1320px] px-8 py-8">
        <Stack className="grid w-full grid-cols-1 items-center gap-10 lg:grid-cols-[0.95fr_1.05fr] xl:gap-16">
          <Stack gap="none" className="max-w-[540px] items-start">
            <Stack direction="row" align="center" gap="sm" className="mb-5">
              <span aria-hidden className="h-px w-7 bg-orange-50" />
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-60">
                Profitability reports
              </p>
            </Stack>
            <h1
              id="profitability-preview-title"
              className="font-manrope text-[clamp(36px,3.5vw,50px)] font-extrabold leading-[1.12] tracking-[-0.045em]"
            >
              See what you earn.
              <br />
              <span className="text-orange-60">Know what you keep.</span>
            </h1>
            <p className="mt-5 max-w-[460px] text-[16px] leading-7 text-secondary-text">
              See revenue, costs, and profit in one report. Choose your dates,
              review the numbers, and ask questions about your results.
            </p>
            <Stack gap="sm" className="mt-7">
              <Button
                size="lg"
                onClick={requestAccess}
                rightIcon={<ArrowRight aria-hidden />}
                className="h-14 rounded-xl bg-orange-60 px-7 text-[16px] text-white shadow-sm hover:bg-orange-70 focus-visible:ring-2 focus-visible:ring-orange-60 focus-visible:ring-offset-4 active:bg-orange-80"
              >
                Get profitability reports
              </Button>
              <p className="max-w-[340px] text-[12px] leading-5 text-secondary-text">
                Chat with our team about setup for your account.
              </p>
            </Stack>
          </Stack>

          <ProfitabilityReportPreview />
        </Stack>

        <Stack className="mt-7 grid w-full grid-cols-1 gap-7 border-t border-stroke-secondary/60 pt-8 md:grid-cols-2 xl:grid-cols-4">
          {benefits.map(({ icon: Icon, title, description }) => (
            <Stack key={title} direction="row" align="start" gap="sm">
              <Icon
                className="mt-0.5 h-4 w-4 shrink-0 text-orange-60"
                aria-hidden
              />
              <Stack gap="xs">
                <h2 className="text-[14px] font-semibold leading-5">{title}</h2>
                <p className="max-w-[360px] text-[13px] leading-[1.65] text-secondary-text">
                  {description}
                </p>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </section>
  );
};
